<!-- Responsibility: Render the generic modal shell and own modal stack/visibility.
Scope: Modality, close arbitration, and hosted content rendering only. -->
<modal>
  <dialog id="game-modal" class={ modalClass() } onclick={ onBackdrop } oncancel={ onCancel }>
    <div class="modal-content" ontouchstart={ onSwipeStart } ontouchend={ onSwipeEnd }>
      <div if={ state.isSwipeDismissEnabled } class="modal-swipe-handle" aria-hidden="true"></div>

      <div class="gm-body modal-scroll-body" ref="body">
        <h2 if={ state.title }>{ state.title }</h2>
        <raw-html if={ state.desc } content={ state.desc } mode="prose" class="fm-desc"></raw-html>
        <raw-html if={ state.meta } content={ state.meta } mode="prose" class="fm-meta"></raw-html>

        <div class="modal-host" ref="hostShell">
          <div
            if={ state.contentComponent }
            is={ state.contentComponent }
            content={ state.contentData }
            options={ state.options }
            openToken={ state.openToken }
            requestModalClose={ requestClose }
            registerModalApi={ registerHostedApi }
          ></div>
        </div>
      </div>

      <div if={ state.showFooter } class="modal-actions modal-fixed-actions">
        <button if={ state.showCloseButton } class="close-btn modal-close-hit" onclick={ requestClose }>{ state.closeLabel }</button>
      </div>
    </div>
  </dialog>

  <script>
    import { dispatcher } from '../../../app-states.js'
    import { playEffect } from '../../../engine/audio.js'
    import { closeDialogWithMotion, openDialogWithMotion } from '../../helpers/modal-motion.ts'
    import { TOPICS } from "@anyquest/shared-core"
    import { dialogComponents, resolveDialogComponent } from './dialog-registry.ts'
    import RawHtml from '../raw-html.riot'

    const dialogStack = []

    export default {
      components: {
        ...dialogComponents,
        'raw-html': RawHtml,
      },

      state: {
        activeDialog: null,
        contentComponent: null,
        contentData: {},
        options: {},
        openToken: 0,
        isSwipeDismissEnabled: false,
        title: '',
        desc: '',
        meta: '',
        showFooter: true,
        showCloseButton: true,
        closeLabel: 'CLOSE',
      },

      onMounted() {
        this.wasOpen = false
        this.lastOpenToken = 0
        this.unmounted = false
        this.hostedApi = null
        this.ignoreOverlayPop = false
        this.overlayDepth = 0
        this.onOverlayPopState = () => this.handleOverlayPopState()
        window.addEventListener('popstate', this.onOverlayPopState)

        this.handles = [
          dispatcher.on(TOPICS.ui.dialogOpen, (_o, dialog) => this.handleDialogOpen(dialog)),
          dispatcher.on(TOPICS.ui.dialogClose, (_o, options) => this.requestClose('dispatcher', options ?? {})),
        ]
      },

      onUnmounted() {
        this.unmounted = true
        this.handles?.forEach((handle) => dispatcher.off(handle))
        window.removeEventListener('popstate', this.onOverlayPopState)
        this.hostedApi = null
      },

      onUpdated() {
        this.syncDialogDOM()
      },

      modalClass() {
        const variant = String(this.state.options?.variant ?? 'default').trim()
        return variant && variant !== 'default' ? `focus-modal focus-modal-${variant}` : 'focus-modal'
      },

      registerHostedApi(api) {
        this.hostedApi = api && typeof api === 'object' ? api : null
      },

      resolveDialogState(dialog, openToken) {
        const options = dialog?.options ?? {}
        const contentId = String(dialog?.content ?? '').trim()
        // Riot needs a registered tag name here so `is={...}` can mount the hosted dialog body.
        const contentComponent = resolveDialogComponent(contentId)
        if (!contentComponent) console.warn('[modal] Unknown dialog content:', contentId)
        return {
          activeDialog: dialog,
          contentComponent,
          contentData: dialog?.data ?? {},
          options,
          openToken,
          isSwipeDismissEnabled: options.swipeDismiss === true,
          title: String(options.title ?? '').trim(),
          desc: String(options.desc ?? '').trim(),
          meta: String(options.meta ?? '').trim(),
          showFooter: options.showFooter !== false,
          showCloseButton: options.showCloseButton !== false,
          closeLabel: String(options.closeLabel ?? '').trim() || 'CLOSE',
        }
      },

      buildDialogSignature(dialog) {
        try {
          return JSON.stringify({
            content: String(dialog?.content || '').trim(),
            data: this.sanitizeValue(dialog?.data ?? {}),
            options: this.sanitizeValue(dialog?.options ?? {}),
          })
        } catch {
          return String(dialog?.content || '').trim()
        }
      },

      sanitizeValue(value) {
        if (value == null || typeof value !== 'object') return value
        if (Array.isArray(value)) return value.map((entry) => this.sanitizeValue(entry))
        return Object.keys(value).sort().reduce((acc, key) => {
          acc[key] = this.sanitizeValue(value[key])
          return acc
        }, {})
      },

      pushOverlayHistory() {
        const base = history.state && typeof history.state === 'object' ? history.state : {}
        try {
          history.pushState({ ...base, am2Modal: true, am2ModalTs: Date.now() }, '')
          this.overlayDepth += 1
        } catch {}
      },

      consumeOverlayHistoryEntry() {
        if (this.overlayDepth <= 0) return
        this.ignoreOverlayPop = true
        history.back()
      },

      handleDialogOpen(dialog) {
        if (this.unmounted || !dialog?.content) return
        const signature = this.buildDialogSignature(dialog)
        const currentSignature = this.state.activeDialog?.signature || ''

        // Keep modal stacking local to the shell so hosted content only owns dialog semantics.
        if (!this.state.activeDialog) {
          this.pushOverlayHistory()
        } else if (currentSignature !== signature) {
          dialogStack.push(this.state.activeDialog)
          this.pushOverlayHistory()
        }

        const nextToken = Number(this.state.openToken ?? 0) + 1
        this.hostedApi = null
        this.update(this.resolveDialogState({ ...dialog, signature }, nextToken))
        dispatcher.trigger('modal', TOPICS.ui.overlayOpened, { key: 'modal' })
      },

      applyClose(options = {}) {
        if (!this.state.activeDialog) return false
        if (dialogStack.length > 0) {
          const previous = dialogStack.pop()
          const nextToken = Number(this.state.openToken ?? 0) + 1
          this.hostedApi = null
          this.update(this.resolveDialogState(previous, nextToken))
          if (options.fromPop !== true) this.consumeOverlayHistoryEntry()
          return true
        }

        this.hostedApi = null
        this.update({
          activeDialog: null,
          contentComponent: null,
          contentData: {},
          options: {},
          isSwipeDismissEnabled: false,
          title: '',
          desc: '',
          meta: '',
          showFooter: true,
          showCloseButton: true,
          closeLabel: 'CLOSE',
        })
        if (options.fromPop !== true) this.consumeOverlayHistoryEntry()
        dispatcher.trigger('modal', TOPICS.ui.overlayClosed, { key: 'modal' })
        return true
      },

      async canClose(reason, detail) {
        const hook = this.hostedApi?.beforeModalClose
        if (typeof hook !== 'function') return true
        try {
          const allowed = await hook(reason, detail)
          return allowed !== false
        } catch (error) {
          console.error('[modal] beforeModalClose failed', error)
          return false
        }
      },

      shouldPlayCloseFx(reason) {
        return !['submit', 'action-success', 'browser-back'].includes(String(reason || ''))
      },

      async requestClose(reason = 'dismiss', detail = {}) {
        if (this.unmounted || !this.state.activeDialog) return false
        const canClose = await this.canClose(reason, detail)
        if (!canClose) return false
        if (this.shouldPlayCloseFx(reason)) playEffect('ui_click')
        return this.applyClose(detail)
      },

      async handleOverlayPopState() {
        if (this.ignoreOverlayPop) {
          this.ignoreOverlayPop = false
          this.overlayDepth = Math.max(0, this.overlayDepth - 1)
          return
        }
        if (!this.state.activeDialog) return
        const canClose = await this.canClose('browser-back', { fromPop: true })
        if (!canClose) {
          this.pushOverlayHistory()
          return
        }
        this.overlayDepth = Math.max(0, this.overlayDepth - 1)
        this.applyClose({ fromPop: true })
      },

      syncDialogDOM() {
        const dialog = this.$('dialog')
        if (!dialog || this.unmounted) return
        const isOpen = !!this.state.activeDialog
        const token = this.state.openToken

        if (isOpen && !this.wasOpen) {
          openDialogWithMotion(dialog)
          this.resetScroll()
        } else if (!isOpen && this.wasOpen) {
          if (!closeDialogWithMotion(dialog)) dialog.close()
        } else if (isOpen && token !== this.lastOpenToken) {
          this.resetScroll()
        }

        this.wasOpen = isOpen
        this.lastOpenToken = token
      },

      resetScroll() {
        if (this.unmounted) return
        requestAnimationFrame(() => {
          if (this.unmounted) return
          const host = this.$('[ref="hostShell"]')
          if (host) host.scrollTop = 0
        })
      },

      onBackdrop(e) {
        if (e.target === e.currentTarget) this.requestClose('backdrop')
      },

      onCancel(e) {
        e.preventDefault()
        this.requestClose('escape')
      },

      onSwipeStart(e) {
        if (this.unmounted || !this.state.isSwipeDismissEnabled) {
          this.swipeEligible = false
          return
        }
        const host = this.$('[ref="hostShell"]')
        if (host && host.scrollTop > 12) {
          this.swipeEligible = false
          return
        }
        const touch = e?.touches?.[0]
        if (!touch) return
        this.swipeEligible = true
        this.swipeStartY = touch.clientY
        this.swipeStartX = touch.clientX
      },

      onSwipeEnd(e) {
        if (!this.swipeEligible || this.unmounted) return
        this.swipeEligible = false
        const touch = e?.changedTouches?.[0]
        if (!touch) return
        const dy = touch.clientY - this.swipeStartY
        const dx = Math.abs(touch.clientX - this.swipeStartX)
        if (dy > 84 && dy > dx * 1.2) this.requestClose('swipe')
      },
    }
  </script>

  <style>
    modal {
      display: contents;
    }

    .gm-body {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
      min-height: 0;
    }

    .gm-body h2 {
      color: var(--gold);
      font-size: 1.14rem;
      letter-spacing: 0.03em;
      line-height: 1.18;
      font-weight: 760;
      margin: 0;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-md);
      text-transform: none;
    }

    .gm-body .fm-desc,
    .gm-body .fm-meta {
      color: color-mix(in srgb, var(--text-prose) var(--alpha-high), #fff) !important;
      font-size: var(--space-md);
      line-height: 1.72;
      margin: 0;
      font-family: var(--font-narrative);
      display: block;
      white-space: pre-line;
      max-width: 68ch;
    }

    .modal-host {
      overflow-y: auto;
      overflow-x: hidden;
      min-height: 0;
      max-height: min(82vh, 48rem);
      animation: modal-body-rise 0.34s ease-out both;
    }
  </style>
</modal>
